import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import DropDown from "../../components/DropDown";
import Loader from "../../components/Loader/Loader";
import NavigateBack from "../../components/NavigateBack";
import useDetails from "../../hooks/useDetails";
import useSlider from "../../hooks/useSlider";
import { addNotification } from "../../services/services";
import { constant } from "../../utils/constants";
import { toastAlert } from "../../utils/SweetAlert";
import Sidebar from "../sidebar/Sidebar";
import "./notification.css";
import CheckPermission from "../../components/CheckPermission";

const NewNotification = () => {
  const details = useDetails();
  const isSlider = useSlider();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      notifyAll: "true",
      type: constant.NOTIFICATION_TYPE.PUSH,
      subject: "",
      description: "",
      locations: [],
    },
    validationSchema: yup.object().shape({
      notifyAll: yup.boolean(),
      locations: yup.array().when("notifyAll", {
        is: (value) => !value,
        then: () => yup.array().min(1).label("Location"),
      }),
      subject: yup.string().trim().required().label(t("notification.subject")),
      description: yup.string().trim().required().label("Description"),
    }),
    onSubmit: (values) => {
      let body = {
        subject: values?.subject,
        description: values?.description,
        type: values?.type,
        notifyAll: values?.notifyAll,
      };
      if (values?.locations?.length)
        body.locations = values?.locations?.map((i) => i.value);

      mutation.mutate(body);
    },
  });

  const mutation = useMutation({
    mutationFn: (body) => addNotification(body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      resetForm();
      navigate(-1);
    },
  });

  const loadLocations = async () => {
    let array = (await details?.userDetails?.userLocations) ?? [];
    return {
      options: array.map((item) => ({
        value: item?._id,
        label: item?.name,
      })),
      hasMore: false,
    };
  };

  return (
    <div className="mainbox">
      <Sidebar />
      <section
        className={isSlider ? "body-content close" : "body-content open"}
      >
        <div className="container-fluid">
          <NavigateBack>{t("notification.notificationNews")}</NavigateBack>

          <div className="notifications_Box">
            <div>
              <h3 className="fontSize14">
                {t("notification.sendNoticicationTo")}:
              </h3>
              <div className="select_Role d-flex align-items-center flex-wrap">
                <div>
                  <input
                    type="radio"
                    id="allResturant"
                    name="notifyAll"
                    value={"true"}
                    checked={values?.notifyAll == "true"}
                    onChange={(e) => {
                      handleChange(e);
                      if (e.target.checked) setFieldValue("locations", []);
                    }}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="allResturant" className="position_Label free">
                    All Location
                  </label>
                </div>
                <div className="positionLabel_Parent">
                  <input
                    type="radio"
                    id="selectedResturants"
                    name="notifyAll"
                    value={"false"}
                    checked={values?.notifyAll == "false"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label
                    htmlFor="selectedResturants"
                    className="position_Label"
                  >
                    Selected Location
                  </label>
                </div>
              </div>
            </div>
            {values?.notifyAll == "false" && (
              <div className="mt-4">
                <label htmlFor="name" className="companyLabel">
                  Select Location
                </label>
                <DropDown
                  placeholder={t("category.enterLocation")}
                  id="locations"
                  loadOptions={loadLocations}
                  isClearable={false}
                  defaultOptions={true}
                  isMulti={true}
                  onChange={(e) => setFieldValue("locations", e)}
                  value={values?.locations}
                  onBlur={() => setFieldTouched("locations", true)}
                />
                <small className="text-danger ms-1">
                  {touched.locations && errors.locations}
                </small>
              </div>
            )}

            <div className="sendnotification_Parent">
              <h3 className="fontSize14">
                {t("notification.sendNotificationVia")}:
              </h3>
              <div className="select_Role d-flex align-items-center">
                <div>
                  <input
                    type="radio"
                    id="push"
                    name="type"
                    value={constant.NOTIFICATION_TYPE.PUSH}
                    checked={values?.type == constant.NOTIFICATION_TYPE.PUSH}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="push" className="position_Label free">
                    {t("notification.push")}
                  </label>
                </div>
                <div className="positionLabel_Parent">
                  <input
                    type="radio"
                    id="email"
                    name="type"
                    value={constant.NOTIFICATION_TYPE.EMAIL}
                    checked={values?.type == constant.NOTIFICATION_TYPE.EMAIL}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="email" className="position_Label">
                    {t("notification.email")}
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="createPlanform_Child">
                <label htmlFor="subject" className="companyLabel">
                  {t("notification.subject")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  className="commonInput"
                  placeholder={t("notification.enterSubject")}
                  name="subject"
                  value={values?.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <small className="text-danger ms-1">
                  {touched.subject && errors.subject}
                </small>
              </div>
              <div className="createPlanform_Child">
                <label htmlFor="name" className="companyLabel">
                  {t("notification.description")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <textarea
                  id="name"
                  className="commonInput"
                  placeholder={t("notification.writeHere")}
                  rows="4"
                  cols="50"
                  name="description"
                  value={values?.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                ></textarea>

                <small className="text-danger ms-1">
                  {touched.description && errors.description}
                </small>
              </div>
            </div>
 <CheckPermission
              permission={constant.PERMISSIONS.CAN_SEND_NOTIFICATION_AND_NEWS}
            >
              <div className="notificationSend_Btn">
              <button
                className="userBtn btn-theme fontSize18"
                onClick={handleSubmit}
                type="button"
              >
                {t("notification.send")}
              </button>
            </div>
            </CheckPermission>
            
          </div>
        </div>
      </section>
      {mutation.isPending && <Loader />}
    </div>
  );
};

export default NewNotification;
